import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export type RichTextKeyboardDismissFn = () => void;

let activeRichTextKeyboardDismiss: RichTextKeyboardDismissFn | null = null;

/**
 * Dismiss the currently focused native rich-text editor before a form-level
 * action (save, validation, navigation) chooses a different focus target.
 */
export function dismissActiveRichTextKeyboard(): void {
  activeRichTextKeyboardDismiss?.();
}

export type RichTextKeyboardChromeContextValue = {
  setRichTextKeyboardDismiss: (fn: RichTextKeyboardDismissFn | null) => void;
  clearRichTextKeyboardDismiss: (fn: RichTextKeyboardDismissFn) => void;
  dismissRichTextKeyboard: () => void;
  acquireRichTextKeyboardBar: () => void;
  releaseRichTextKeyboardBar: () => void;
  subscribeRichTextKeyboardActive: (
    listener: (active: boolean) => void,
  ) => () => void;
};

export const RichTextKeyboardChromeContext =
  createContext<RichTextKeyboardChromeContextValue | null>(null);

export function RichTextKeyboardChromeProvider({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element {
  const dismissRef = useRef<RichTextKeyboardDismissFn | null>(null);
  const barWantedCountRef = useRef(0);
  const barVisibleRef = useRef(false);
  const listenersRef = useRef(new Set<(active: boolean) => void>());

  const notify = useCallback((active: boolean) => {
    barVisibleRef.current = active;
    listenersRef.current.forEach(listener => {
      listener(active);
    });
  }, []);

  const setRichTextKeyboardDismiss = useCallback(
    (fn: RichTextKeyboardDismissFn | null) => {
      dismissRef.current = fn;
      activeRichTextKeyboardDismiss = fn;
    },
    [],
  );

  const clearRichTextKeyboardDismiss = useCallback(
    (fn: RichTextKeyboardDismissFn) => {
      if (dismissRef.current === fn) {
        dismissRef.current = null;
        if (activeRichTextKeyboardDismiss === fn) {
          activeRichTextKeyboardDismiss = null;
        }
      }
    },
    [],
  );

  const syncBarVisible = useCallback(() => {
    const next = barWantedCountRef.current > 0;
    if (barVisibleRef.current !== next) {
      notify(next);
    }
  }, [notify]);

  const acquireRichTextKeyboardBar = useCallback(() => {
    barWantedCountRef.current += 1;
    syncBarVisible();
  }, [syncBarVisible]);

  const releaseRichTextKeyboardBar = useCallback(() => {
    barWantedCountRef.current = Math.max(0, barWantedCountRef.current - 1);
    syncBarVisible();
  }, [syncBarVisible]);

  const dismissRichTextKeyboard = useCallback(() => {
    dismissRef.current?.();
  }, []);

  const subscribeRichTextKeyboardActive = useCallback(
    (listener: (active: boolean) => void) => {
      listenersRef.current.add(listener);
      listener(barVisibleRef.current);
      return () => {
        listenersRef.current.delete(listener);
      };
    },
    [],
  );

  const value = useMemo(
    () => ({
      setRichTextKeyboardDismiss,
      clearRichTextKeyboardDismiss,
      dismissRichTextKeyboard,
      acquireRichTextKeyboardBar,
      releaseRichTextKeyboardBar,
      subscribeRichTextKeyboardActive,
    }),
    [
      setRichTextKeyboardDismiss,
      clearRichTextKeyboardDismiss,
      dismissRichTextKeyboard,
      acquireRichTextKeyboardBar,
      releaseRichTextKeyboardBar,
      subscribeRichTextKeyboardActive,
    ],
  );

  return (
    <RichTextKeyboardChromeContext.Provider value={value}>
      {children}
    </RichTextKeyboardChromeContext.Provider>
  );
}

/** Keep the WKWebView dismiss callback alive for the editor instance lifetime. */
export function useRegisterRichTextKeyboardDismiss(
  dismiss: RichTextKeyboardDismissFn,
): void {
  const ctx = useContext(RichTextKeyboardChromeContext);

  useEffect(() => {
    if (!ctx) {
      return undefined;
    }
    ctx.setRichTextKeyboardDismiss(dismiss);
    return () => {
      ctx.clearRichTextKeyboardDismiss(dismiss);
    };
  }, [ctx, dismiss]);
}

/** Controls the single native Done bar. Refcount so unit screens with
 * several 10tap fields cannot wipe the focused editor (1106 issue 26). */
export function useRichTextKeyboardBarVisible(visible: boolean): void {
  const ctx = useContext(RichTextKeyboardChromeContext);

  useEffect(() => {
    if (!ctx || !visible) {
      return undefined;
    }
    ctx.acquireRichTextKeyboardBar();
    return () => {
      ctx.releaseRichTextKeyboardBar();
    };
  }, [ctx, visible]);
}

export function useRichTextKeyboardDismissBar(): {
  active: boolean;
  dismiss: () => void;
} {
  const ctx = useContext(RichTextKeyboardChromeContext);
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (!ctx) {
      return undefined;
    }
    return ctx.subscribeRichTextKeyboardActive(setActive);
  }, [ctx]);

  return {
    active,
    dismiss: ctx?.dismissRichTextKeyboard ?? noopDismiss,
  };
}

function noopDismiss(): void {
  // Screens without RichTextKeyboardChromeProvider have nothing to dismiss.
}
